import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import {
  submitEngineeredGoodForm,
  ENGINEERED_GOOD_ORG_TYPES,
  ENGINEERED_GOOD_INTERESTS,
  ENGINEERED_GOOD_SIZES,
} from "@/lib/contact.functions";

const formSchema = z.object({
  name: z.string().trim().min(2, "Please enter your full name."),
  jobTitle: z.string().trim().optional(),
  email: z.string().trim().email("Please enter a valid email address."),
  phone: z.string().trim().optional(),
  company: z.string().trim().min(2, "Please enter your organization's name."),
  website: z.string().trim().optional(),
  // Unchecked radios report null, so a plain required_error never fires.
  orgType: z.enum(ENGINEERED_GOOD_ORG_TYPES, { errorMap: () => ({ message: "Pick the one that fits best." }) }),
  interests: z.array(z.enum(ENGINEERED_GOOD_INTERESTS)).min(1, "Pick at least one."),
  cause: z.string().trim().optional(),
  size: z.union([z.enum(ENGINEERED_GOOD_SIZES), z.literal("")]).optional(),
  message: z.string().trim().min(10, "Tell us a little more, a sentence or two is plenty."),
});

type FormValues = z.infer<typeof formSchema>;

const LABEL_CLASS = "mb-2 block font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground";
const INPUT_CLASS =
  "w-full rounded-sm border border-border bg-background px-3.5 py-3 text-base text-foreground caret-sun placeholder:text-muted-foreground/60 transition-colors focus:border-sun focus:outline-none focus-visible:ring-2 focus-visible:ring-sun/40 aria-[invalid=true]:border-red-400";
const CHIP_CLASS =
  "flex min-h-11 cursor-pointer items-center gap-3 rounded-sm border px-4 py-2.5 text-sm text-foreground transition-colors hover:border-sun/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-sun/40";
// Driven by form state rather than :has(:checked) so older Safari shows the selection too.
function chipClass(on: boolean) {
  return `${CHIP_CLASS} ${on ? "border-sun bg-sun/10" : "border-border bg-background"}`;
}

function Required() {
  return <span className="text-sun"> *</span>;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1.5 text-sm text-red-400">{message}</p>;
}

function Group({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-5 border-t border-border pt-8 first:border-t-0 first:pt-0">
      <legend className="float-left mb-1 flex w-full items-center gap-3 font-display text-lg font-black uppercase leading-none text-foreground [word-spacing:0.15em]">
        <span className="font-mono text-[0.7rem] font-normal tracking-[0.22em] text-sun">{n}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

export function EngineeredGoodForm() {
  const submit = useServerFn(submitEngineeredGoodForm);
  const startedAt = useRef(Date.now());
  const [hp, setHp] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  useEffect(() => {
    startedAt.current = Date.now();
  }, []);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      jobTitle: "",
      email: "",
      phone: "",
      company: "",
      website: "",
      interests: [],
      cause: "",
      size: "",
      message: "",
    },
  });
  const errors = form.formState.errors;
  const orgType = form.watch("orgType");
  const interests = form.watch("interests") ?? [];

  async function onSubmit(values: FormValues) {
    try {
      await submit({
        data: {
          ...values,
          size: values.size || undefined,
          hp,
          elapsedMs: Date.now() - startedAt.current,
        },
      });
      setSentTo(values.company);
      form.reset();
    } catch (error) {
      console.error("Engineering Good form submission failed", error);
      toast.error(
        "Something went wrong sending your note. Please email solutions@sunsmartengineering.com.",
      );
    }
  }

  if (sentTo) {
    return (
      <div className="rounded-sm border border-sun/40 bg-sun/5 p-8 md:p-12" role="status">
        <div className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun">Note received</div>
        <h3 className="mt-4 font-display text-3xl font-black uppercase leading-[0.95] text-foreground">
          Thanks. We'll be in touch.
        </h3>
        <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground">
          Our team has your note about {sentTo}. Expect a reply within one business
          day to set up a call.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-10" noValidate>
      <Group n="A" title="About you">
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="block">
            <span className={LABEL_CLASS}>
              Name
              <Required />
            </span>
            <input
              type="text"
              autoComplete="name"
              placeholder="Jane Rivera"
              aria-invalid={!!errors.name}
              className={INPUT_CLASS}
              {...form.register("name")}
            />
            <FieldError message={errors.name?.message} />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>Title or role</span>
            <input
              type="text"
              autoComplete="organization-title"
              placeholder="Executive Director"
              className={INPUT_CLASS}
              {...form.register("jobTitle")}
            />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>
              Email
              <Required />
            </span>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@organization.org"
              aria-invalid={!!errors.email}
              className={INPUT_CLASS}
              {...form.register("email")}
            />
            <FieldError message={errors.email?.message} />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>Phone</span>
            <input
              type="tel"
              autoComplete="tel"
              placeholder="(555) 555-5555"
              className={INPUT_CLASS}
              {...form.register("phone")}
            />
          </label>
        </div>
      </Group>

      <Group n="B" title="Your organization">
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="block">
            <span className={LABEL_CLASS}>
              Organization
              <Required />
            </span>
            <input
              type="text"
              autoComplete="organization"
              placeholder="Association, nonprofit or company"
              aria-invalid={!!errors.company}
              className={INPUT_CLASS}
              {...form.register("company")}
            />
            <FieldError message={errors.company?.message} />
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>Website</span>
            <input
              type="text"
              inputMode="url"
              autoComplete="url"
              placeholder="organization.org"
              className={INPUT_CLASS}
              {...form.register("website")}
            />
          </label>
        </div>

        <div role="radiogroup" aria-labelledby="eg-org-type" aria-invalid={!!errors.orgType}>
          <span id="eg-org-type" className={LABEL_CLASS}>
            Which best describes you?
            <Required />
          </span>
          <div className="grid gap-2 sm:grid-cols-2">
            {ENGINEERED_GOOD_ORG_TYPES.map((type) => (
              <label key={type} className={chipClass(orgType === type)}>
                <input type="radio" value={type} className="accent-sun" {...form.register("orgType")} />
                {type}
              </label>
            ))}
          </div>
          <FieldError message={errors.orgType?.message} />
        </div>

        <label className="block sm:max-w-xs">
          <span className={LABEL_CLASS}>Members or team size</span>
          <select className={INPUT_CLASS} {...form.register("size")}>
            <option value="">Choose one</option>
            {ENGINEERED_GOOD_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </Group>

      <Group n="C" title="Working together">
        <div role="group" aria-labelledby="eg-interests">
          <span id="eg-interests" className={LABEL_CLASS}>
            How would you like to get involved?
            <Required />
          </span>
          <div className="grid gap-2">
            {ENGINEERED_GOOD_INTERESTS.map((interest) => (
              <label key={interest} className={chipClass(interests.includes(interest))}>
                <input
                  type="checkbox"
                  value={interest}
                  className="accent-sun"
                  {...form.register("interests")}
                />
                {interest}
              </label>
            ))}
          </div>
          <FieldError message={errors.interests?.message} />
        </div>

        <label className="block">
          <span className={LABEL_CLASS}>Cause or charity you'd like to support</span>
          <input
            type="text"
            placeholder="e.g. Every Child A Swimmer"
            className={INPUT_CLASS}
            {...form.register("cause")}
          />
        </label>

        <label className="block">
          <span className={LABEL_CLASS}>
            Tell us about your organization
            <Required />
          </span>
          <textarea
            rows={5}
            placeholder="Who you serve, what your members build, and what you'd like this partnership to fund."
            aria-invalid={!!errors.message}
            className={INPUT_CLASS}
            {...form.register("message")}
          />
          <FieldError message={errors.message?.message} />
        </label>
      </Group>

      <div className="flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Goes straight to our team.</p>
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-60 disabled:hover:scale-100 sm:w-fit"
        >
          {form.formState.isSubmitting ? (
            <>
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-ink/40 border-t-ink" />
              Sending...
            </>
          ) : (
            <>
              Start the conversation <span aria-hidden>→</span>
            </>
          )}
        </button>
      </div>
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Leave this field empty
          <input
            type="text"
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
          />
        </label>
      </div>
    </form>
  );
}
